/**
 * NewPlatformConnectionPage — /organization/platforms/new
 * Create a data platform on the FormPage template. Demo pages: /form-layouts (sections, footer actions) and
 * /connected-apps. Server route createIntelPlatform: `CanManageIntegrations`.
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import {
  createIntelPlatformMutation,
  listIntelPlatformsQueryKey,
} from '../../generated/api/@tanstack/react-query.gen.js';
import { zIntelPlatformCreate } from '../../generated/api/zod.gen.js';
import {
  Form,
  FormActions,
  FormSection,
  SelectFormField,
  TextFormField,
  useZodForm,
} from '../../components/form/index.js';
import { FormPage } from '../../components/page/index.js';
import { Can } from '../../components/auth/Can.js';
import { useToast } from '../../components/ui/Toast.js';
import { PAGE_HELP } from '../../help/index.js';
import { PLATFORM_CATEGORIES, platformCategoryLabel } from './platformLabels.js';

const platformSchema = zIntelPlatformCreate.pick({ slug: true, name: true, category: true, config: true });

const CATEGORY_OPTIONS = PLATFORM_CATEGORIES.map((value) => ({ value, label: platformCategoryLabel(value) }));

export const NewPlatformConnectionPage: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { notify } = useToast();
  const create = useMutation(createIntelPlatformMutation());
  const form = useZodForm(platformSchema, {
    defaultValues: { slug: '', name: '', category: 'social', config: { api_key_env: '' } },
  });

  const submit = async (values: z.output<typeof platformSchema>) => {
    await create.mutateAsync({ body: values });
    await queryClient.invalidateQueries({ queryKey: listIntelPlatformsQueryKey() });
    notify(`${values.name} added.`);
    navigate('/organization/platforms');
  };

  return (
    <FormPage
      title="Add platform"
      subtitle="Connect a data platform Conflui can collect from."
      helpContent={PAGE_HELP['platform-connections']}
      backTo="/organization/platforms"
      backLabel="Platform connections"
    >
      <Can policy="CanManageIntegrations">
        <Form form={form} onSubmit={submit} guardUnsaved>
          <FormSection
            title="Platform details"
            description="Keys stay in your server environment: only the variable name is stored here."
          >
            <TextFormField name="name" label="Name" placeholder="YouTube" required />
            <TextFormField
              name="slug"
              label="Identifier"
              placeholder="youtube"
              required
              description="Lowercase, no spaces."
            />
            <SelectFormField name="category" label="Category" options={CATEGORY_OPTIONS} required />
            <TextFormField name="config.api_key_env" label="API key variable" placeholder="YOUTUBE_API_KEY" />
            <FormActions submitLabel="Add platform" cancelTo="/organization/platforms" />
          </FormSection>
        </Form>
      </Can>
    </FormPage>
  );
};
